import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
} from "@mui/material";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import CalendarView from "../components/CalendarView";
import BookingForm from "../components/BookingForm";
import BookingConfirmation from "../components/BookingConfirmation";
import { bookingService } from "../services/bookingService";
import { dateUtils } from "../utils/dateUtils";

const steps = ["Seleziona Data e Orario", "Inserisci i tuoi Dati", "Conferma Prenotazione"];

function LessonBooking() {
  const [activeStep, setActiveStep] = useState(0);
  const [lessonTypes, setLessonTypes] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingData, setBookingData] = useState({
    userName: "",
    userEmail: "",
    userPhone: "",
    notes: "",
    hasCarnet: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Load lesson types on component mount
  useEffect(() => {
    loadLessonTypes();
    loadAvailableSlots();
  }, []);

  const loadLessonTypes = async () => {
    try {
      const types = await bookingService.getLessonTypes();
      setLessonTypes(types);
    } catch (error) {
      console.error("Error loading lesson types:", error);
      setError("Errore nel caricamento dei tipi di lezione");
    }
  };

  const loadAvailableSlots = async () => {
    try {
      setLoading(true);
      const { start, end } = dateUtils.getDateRange(30); // Next 30 days
      const slots = await bookingService.getAvailableSlots(start, end);
      setAvailableSlots(slots);
    } catch (error) {
      console.error("Error loading available slots:", error);
      setError("Errore nel caricamento degli slot disponibili");
    } finally {
      setLoading(false);
    }
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setError("");
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!selectedSlot) {
        setError("Seleziona una data e un orario per continuare");
        return;
      }
      // Scroll al riepilogo quando si passa allo step 1
      setActiveStep((prevStep) => prevStep + 1);
      setTimeout(() => {
        const element = document.getElementById('booking-summary');
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
      return;
    } else if (activeStep === 1) {
      // Detailed validation with specific error messages
      if (!bookingData.userName?.trim()) {
        setError("📝 Il campo 'Nome e Cognome' è obbligatorio");
        return;
      }

      if (!bookingData.userEmail?.trim()) {
        setError("📧 Il campo 'Email' è obbligatorio");
        return;
      }

      if (!bookingData.userPhone?.trim()) {
        setError("📱 Il campo 'Telefono' è obbligatorio");
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(bookingData.userEmail)) {
        setError("📧 L'indirizzo email inserito non è valido (esempio: nome@email.com)");
        return;
      }

      // Validate phone format (Italian phone number)
      const phoneRegex = /^[+]?[\d\s\-()]{8,}$/;
      if (!phoneRegex.test(bookingData.userPhone)) {
        setError("📱 Il numero di telefono non è valido (deve contenere almeno 8 cifre)");
        return;
      }
      // Scroll alla conferma quando si passa allo step 2
      setActiveStep((prevStep) => prevStep + 1);
      setTimeout(() => {
        const element = document.getElementById('confirmation-header');
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
      return;
    }

    setError("");
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setError("");
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleBookingDataChange = (field, value) => {
    setBookingData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleConfirmBooking = async () => {
    try {
      setLoading(true);
      setError("");

      // Create booking
      const bookingPayload = {
        ...bookingData,
        lessonTypeId: selectedSlot.lessonTypeId,
        date: selectedSlot.date,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
      };

      const newBooking = await bookingService.createBooking(bookingPayload);

      // Refresh available slots so the UI shows updated availability
      await loadAvailableSlots();

      setSuccess("Prenotazione inviata con successo! Riceverai una conferma via email.");

      // Auto advance to success step
      setTimeout(() => {
        setActiveStep(3);
        // Scroll al messaggio di successo
        setTimeout(() => {
          const element = document.getElementById('booking-success');
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 100);
      }, 1000);

    } catch (error) {
      console.error("Error creating booking:", error);
      setError("Errore durante la creazione della prenotazione. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const handleNewBooking = () => {
    setActiveStep(0);
    setSelectedSlot(null);
    setBookingData({
      userName: "",
      userEmail: "",
      userPhone: "",
      notes: "",
      hasCarnet: false,
    });
    setError("");
    setSuccess("");
    loadAvailableSlots(); // Refresh available slots
  };

  const getSelectedLessonType = () => {
    if (!selectedSlot) return null;

    // Try direct ID match first
    let lessonType = lessonTypes.find((type) => type.id === selectedSlot.lessonTypeId);

    // If not found, try matching by name (fallback for hardcoded IDs)
    if (!lessonType) {
      const nameMap = {
        'coaching-individuale': 'Coaching Individuale',
        'coaching-gruppo': 'Coaching di Gruppo',
        'consulenza-breve': 'Consulenza Breve',
        'workshop-tematico': 'Workshop Tematico'
      };

      const expectedName = nameMap[selectedSlot.lessonTypeId];
      if (expectedName) {
        lessonType = lessonTypes.find((type) => type.name === expectedName);
      }
    }

    // Debug log (removed for production)

    return lessonType;
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <CalendarView
            availableSlots={availableSlots}
            lessonTypes={lessonTypes}
            selectedSlot={selectedSlot}
            onSlotSelect={handleSlotSelect}
            loading={loading}
          />
        );

      case 1:
        return (
          <BookingForm
            bookingData={bookingData}
            selectedSlot={selectedSlot}
            lessonType={getSelectedLessonType()}
            onChange={handleBookingDataChange}
          />
        );

      case 2:
        return (
          <BookingConfirmation
            bookingData={bookingData}
            selectedSlot={selectedSlot}
            lessonType={getSelectedLessonType()}
            onConfirm={handleConfirmBooking}
            loading={loading}
          />
        );

      case 3:
        return (
          <Box id="booking-success" textAlign="center" py={4} sx={{ scrollMarginTop: '100px' }}>
            <Typography variant="h4" color="primary" gutterBottom>
              Prenotazione Completata!
            </Typography>
            <Typography variant="body1" paragraph>
              La tua richiesta di prenotazione è stata inviata con successo.
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
              Riceverai una conferma via email una volta che la prenotazione sarà stata approvata.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={handleNewBooking}
              sx={{ mt: 2 }}
            >
              Nuova Prenotazione
            </Button>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SEO
        title="Prenota una Lezione - Ilaria Stumpo Coaching"
        description="Inizia il tuo viaggio interiore personalizzato con Ilaria Stumpo. Scegli data, orario e tipo di lezione che preferisci."
        keywords="prenotazione lezioni, coaching, Ilaria Stumpo, sessioni personalizzate"
      />

      <Header />

      <Container maxWidth="lg" sx={{ pt: 12, pb: 4 }}>
        <Box mb={4} textAlign="center">
          <Typography variant="h3" component="h1" gutterBottom>
            Prenota la tua Lezione
          </Typography>
        </Box>

        <Paper elevation={3} sx={{ p: { xs: 2, md: 4 } }}>
          {activeStep < 3 && (
            <Box mb={4}>
              <Stepper activeStep={activeStep} alternativeLabel>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box minHeight="400px">
            {renderStepContent()}
          </Box>

          {activeStep < 3 && activeStep !== 2 && (
            <Box mt={4} display="flex" justifyContent="space-between">
              <Button
                disabled={activeStep === 0}
                onClick={handleBack}
                variant="outlined"
              >
                Indietro
              </Button>

              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading}
              >
                Avanti
              </Button>
            </Box>
          )}
        </Paper>
      </Container>

      <Footer />
    </>
  );
}

export default LessonBooking;